import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject
} from '@angular/core';
import { forkJoin } from 'rxjs';
import { CompanyVendor, ViveProgress } from 'src/app/classes/models';
import { WidgetPanelCounts } from 'src/app/interfaces/widget-panel-counts';
import { CompanyVendorService } from 'src/app/services/company-vendor.service';
import { ViveProgressService } from 'src/app/services/vive-progress.service';

@Component({
  selector: 'app-pending-reviews-widget',
  templateUrl: './pending-reviews-widget.component.html',
  styleUrls: ['./pending-reviews-widget.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class PendingReviewsWidgetComponent {
  private _cdr = inject(ChangeDetectorRef);
  pendingReviews: WidgetPanelCounts['data'] = new Map([]);

  widgetPanelCounts: WidgetPanelCounts = {
    title: 'Pending Reviews',
    hasData: false,
    data: this.pendingReviews
  };

  currentDate: Date = new Date();
  thirtyDaysAgo: Date = new Date(
    new Date().setDate(this.currentDate.getDate() - 30)
  );
  fortyFiveDaysAgo: Date = new Date(
    new Date().setDate(this.currentDate.getDate() - 45)
  );

  getCountOfSiteConditionalVendorsNeedInsuranceReview: {} = {
    options: JSON.stringify({ distinct: true }),
    viveStatus: JSON.stringify({
      $in: ['Reviewing']
    }),
    Vendor: JSON.stringify({
      options: { required: true },
      CompanyVendorStatusChange: {
        options: { required: true },
        $or: [
          { oldViveStatus: 'Site Conditional' },
          { newViveStatus: 'Site Conditional' }
        ]
      },
      ViveProgress: {
        options: { required: true },
        $or: [
          {
            type: 'Final Review',
            status: { $notIn: ['Passed', 'Failed'] }
          },
          {
            type: { $ne: 'Final Review' }
          }
        ]
      }
    })
  };

  getBusinessReview30DaysOrMore: ViveProgress | {} = {
    options: JSON.stringify({ required: true }),
    status: 'Reviewing',
    type: 'Vendor Info',
    updatedAt: JSON.stringify({
      $lte: this.thirtyDaysAgo
    }),
    literals: JSON.stringify({
      $and: ['companyVendorStatusWhereClauseLiteral']
    })
  };

  getBusinessResolving30DaysOrMore: ViveProgress | {} = {
    options: JSON.stringify({ required: true }),
    status: 'Resolving',
    type: 'Vendor Info',
    updatedAt: JSON.stringify({
      $lte: this.thirtyDaysAgo
    }),
    literals: JSON.stringify({
      $and: ['companyVendorStatusWhereClauseLiteral']
    })
  };

  getOwnersReview30DaysOrMore: ViveProgress | {} = {
    options: JSON.stringify({ required: true }),
    status: 'Reviewing',
    type: 'Owner Info',
    updatedAt: JSON.stringify({
      $lte: this.thirtyDaysAgo
    }),
    literals: JSON.stringify({
      $and: ['companyVendorStatusWhereClauseLiteral']
    })
  };

  getOwnersResolving30DaysOrMore: ViveProgress | {} = {
    options: JSON.stringify({ required: true }),
    status: 'Resolving',
    type: 'Owner Info',
    updatedAt: JSON.stringify({
      $lte: this.thirtyDaysAgo
    }),
    literals: JSON.stringify({
      $and: ['companyVendorStatusWhereClauseLiteral']
    })
  };

  getTradesReview30DaysOrMore: ViveProgress | {} = {
    options: JSON.stringify({ required: true }),
    status: 'Reviewing',
    type: 'Trades & Licenses',
    updatedAt: JSON.stringify({
      $lte: this.thirtyDaysAgo
    }),
    literals: JSON.stringify({
      $and: ['companyVendorStatusWhereClauseLiteral']
    })
  };

  getTradesResolving30DaysOrMore: ViveProgress | {} = {
    options: JSON.stringify({ required: true }),
    status: 'Resolving',
    type: 'Trades & Licenses',
    updatedAt: JSON.stringify({
      $lte: this.thirtyDaysAgo
    }),
    literals: JSON.stringify({
      $and: ['companyVendorStatusWhereClauseLiteral']
    })
  };

  getInsuranceReview30DaysOrMore: ViveProgress | {} = {
    options: JSON.stringify({ required: true }),
    status: 'Reviewing',
    type: 'Insurance',
    updatedAt: JSON.stringify({
      $between: [this.thirtyDaysAgo, this.fortyFiveDaysAgo]
    }),
    literals: JSON.stringify({
      $and: ['companyVendorStatusWhereClauseLiteral']
    })
  };

  getInsuranceReview45DaysOrMore: ViveProgress | {} = {
    options: JSON.stringify({ required: true }),
    status: 'Reviewing',
    type: 'Insurance',
    updatedAt: JSON.stringify({
      $lte: this.fortyFiveDaysAgo
    }),
    literals: JSON.stringify({
      $and: ['companyVendorStatusWhereClauseLiteral']
    })
  };

  getInsuranceResolving30DaysOrMore: ViveProgress | {} = {
    options: JSON.stringify({ required: true }),
    status: 'Resolving',
    type: 'Insurance',
    updatedAt: JSON.stringify({
      $between: [this.thirtyDaysAgo, this.fortyFiveDaysAgo]
    }),
    literals: JSON.stringify({
      $and: ['companyVendorStatusWhereClauseLiteral']
    })
  };

  getInsuranceResolving45DaysOrMore: ViveProgress | {} = {
    options: JSON.stringify({ required: true }),
    status: 'Resolving',
    type: 'Insurance',
    updatedAt: JSON.stringify({
      $lte: this.fortyFiveDaysAgo
    }),
    literals: JSON.stringify({
      $and: ['companyVendorStatusWhereClauseLiteral']
    })
  };

  getInsuranceMissing30DaysOrMore: ViveProgress | {} = {
    options: JSON.stringify({ required: true }),
    status: 'Missing',
    type: 'Insurance',
    updatedAt: JSON.stringify({
      $between: [this.thirtyDaysAgo, this.fortyFiveDaysAgo]
    }),
    literals: JSON.stringify({
      $and: ['companyVendorStatusWhereClauseLiteral']
    })
  };

  getInsuranceMissing45DaysOrMore: ViveProgress | {} = {
    options: JSON.stringify({ required: true }),
    status: 'Missing',
    type: 'Insurance',
    updatedAt: JSON.stringify({
      $lte: this.fortyFiveDaysAgo
    }),
    literals: JSON.stringify({
      $and: ['companyVendorStatusWhereClauseLiteral']
    })
  };

  getVendorViveProgressReviewingUpdatedAtCounts30DaysOrMore:
    CompanyVendor | {} = {
    viveStatus: JSON.stringify({
      $notIn: ['Inactive', 'Unregistered', 'Registering', 'Awaiting Payment']
    }),
    literals: JSON.stringify({
      $and: ['getVendorViveProgressReviewingUpdatedAtCounts']
    }),
    Vendor: JSON.stringify({
      options: { required: true },
      ViveProgress: {
        options: { required: true },
        type: 'Final Review',
        status: 'Reviewing',
        updatedAt: {
          $between: [this.thirtyDaysAgo, this.fortyFiveDaysAgo]
        }
      }
    })
  };

  getVendorViveProgressReviewingUpdatedAtCounts45DaysOrMore:
    CompanyVendor | {} = {
    viveStatus: JSON.stringify({
      $notIn: ['Inactive', 'Unregistered', 'Registering', 'Awaiting Payment']
    }),
    literals: JSON.stringify({
      $and: ['getVendorViveProgressReviewingUpdatedAtCounts']
    }),
    Vendor: JSON.stringify({
      options: { required: true },
      ViveProgress: {
        options: { required: true },
        type: 'Final Review',
        status: 'Reviewing',
        updatedAt: {
          $lte: this.fortyFiveDaysAgo
        }
      }
    })
  };

  constructor(
    private CompanyVendorService: CompanyVendorService,
    private ViveProgressService: ViveProgressService
  ) {
    forkJoin([
      this.getCompanyVendorCount(
        this.getCountOfSiteConditionalVendorsNeedInsuranceReview
      ),
      this.getViveProgressCount(this.getBusinessReview30DaysOrMore),
      this.getViveProgressCount(this.getBusinessResolving30DaysOrMore),
      this.getViveProgressCount(this.getOwnersReview30DaysOrMore),
      this.getViveProgressCount(this.getOwnersResolving30DaysOrMore),
      this.getViveProgressCount(this.getTradesReview30DaysOrMore),
      this.getViveProgressCount(this.getTradesResolving30DaysOrMore),
      this.getViveProgressCount(this.getInsuranceReview30DaysOrMore),
      this.getViveProgressCount(this.getInsuranceReview45DaysOrMore),
      this.getViveProgressCount(this.getInsuranceResolving30DaysOrMore),
      this.getViveProgressCount(this.getInsuranceResolving45DaysOrMore),
      this.getViveProgressCount(this.getInsuranceMissing30DaysOrMore),
      this.getViveProgressCount(this.getInsuranceMissing45DaysOrMore),
      this.getCompanyVendorCount(
        this.getVendorViveProgressReviewingUpdatedAtCounts30DaysOrMore
      ),
      this.getCompanyVendorCount(
        this.getVendorViveProgressReviewingUpdatedAtCounts45DaysOrMore
      )
    ]).subscribe({
      next: ([
        getCountOfSiteConditionalVendorsNeedInsuranceReview,
        getBusinessReview30DaysOrMore,
        getBusinessResolving30DaysOrMore,
        getOwnersReview30DaysOrMore,
        getOwnersResolving30DaysOrMore,
        getTradesReview30DaysOrMore,
        getTradesResolving30DaysOrMore,
        getInsuranceReview30DaysOrMore,
        getInsuranceReview45DaysOrMore,
        getInsuranceResolving30DaysOrMore,
        getInsuranceResolving45DaysOrMore,
        getInsuranceMissing30DaysOrMore,
        getInsuranceMissing45DaysOrMore,
        getVendorViveProgressReviewingUpdatedAtCounts30DaysOrMore,
        getVendorViveProgressReviewingUpdatedAtCounts45DaysOrMore
      ]) => {
        this.pendingReviews.set('Site Conditional Vendors That Renewed', {
          count: Number(getCountOfSiteConditionalVendorsNeedInsuranceReview),
          query: this.getCountOfSiteConditionalVendorsNeedInsuranceReview
        });
        this.pendingReviews.set('Business Review ≥ 30', {
          count: Number(getBusinessReview30DaysOrMore),
          query: {
            ViveProgress: JSON.stringify(this.getBusinessReview30DaysOrMore)
          }
        });
        this.pendingReviews.set('Business Resolving ≥ 30', {
          count: Number(getBusinessResolving30DaysOrMore),
          query: {
            ViveProgress: JSON.stringify(this.getBusinessResolving30DaysOrMore)
          }
        });
        this.pendingReviews.set('Owner Review ≥ 30', {
          count: Number(getOwnersReview30DaysOrMore),
          query: {
            ViveProgress: JSON.stringify(this.getOwnersReview30DaysOrMore)
          }
        });
        this.pendingReviews.set('Owner Resolving ≥ 30', {
          count: Number(getOwnersResolving30DaysOrMore),
          query: {
            ViveProgress: JSON.stringify(this.getOwnersResolving30DaysOrMore)
          }
        });
        this.pendingReviews.set('Trade Review ≥ 30', {
          count: Number(getTradesReview30DaysOrMore),
          query: {
            ViveProgress: JSON.stringify(this.getTradesReview30DaysOrMore)
          }
        });
        this.pendingReviews.set('Trade Resolving ≥ 30', {
          count: Number(getTradesResolving30DaysOrMore),
          query: {
            ViveProgress: JSON.stringify(this.getTradesResolving30DaysOrMore)
          }
        });
        this.pendingReviews.set('Insurance Review ≥ 30', {
          count: Number(getInsuranceReview30DaysOrMore),
          query: {
            ViveProgress: JSON.stringify(this.getInsuranceReview30DaysOrMore)
          }
        });
        this.pendingReviews.set('Insurance Review ≥ 45', {
          count: Number(getInsuranceReview45DaysOrMore),
          query: {
            ViveProgress: JSON.stringify(this.getInsuranceReview45DaysOrMore)
          }
        });
        this.pendingReviews.set('Insurance Resolving ≥ 30', {
          count: Number(getInsuranceResolving30DaysOrMore),
          query: {
            ViveProgress: JSON.stringify(this.getInsuranceMissing30DaysOrMore)
          }
        });
        this.pendingReviews.set('Insurance Resolving ≥ 45', {
          count: Number(getInsuranceResolving45DaysOrMore),
          query: {
            ViveProgress: JSON.stringify(this.getInsuranceResolving45DaysOrMore)
          }
        });
        this.pendingReviews.set('Insurance Missing ≥ 30', {
          count: Number(getInsuranceMissing30DaysOrMore),
          query: {
            ViveProgress: JSON.stringify(this.getInsuranceMissing30DaysOrMore)
          }
        });
        this.pendingReviews.set('Insurance Missing ≥ 45', {
          count: Number(getInsuranceMissing45DaysOrMore),
          query: {
            ViveProgress: JSON.stringify(this.getInsuranceMissing45DaysOrMore)
          }
        });
        this.pendingReviews.set('Compliance Reviewing ≥ 30', {
          count: Number(
            getVendorViveProgressReviewingUpdatedAtCounts30DaysOrMore
          ),
          query: this.getVendorViveProgressReviewingUpdatedAtCounts30DaysOrMore
        });
        this.pendingReviews.set('Compliance Reviewing ≥ 45', {
          count: Number(
            getVendorViveProgressReviewingUpdatedAtCounts45DaysOrMore
          ),
          query: this.getVendorViveProgressReviewingUpdatedAtCounts45DaysOrMore
        });
        this._cdr.markForCheck();
      }
    });
  }

  private getViveProgressCount(query: {}) {
    return this.ViveProgressService.getViveProgressCount(query);
  }

  private getCompanyVendorCount(query: {}) {
    return this.CompanyVendorService.getCompanyVendorCount(query);
  }
}
